import { useState, useRef, useEffect } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import cls from 'classnames';
import IconChevronLeftLarge from '@picsart/icons/IconChevronLeftLarge';
import IconChevronRightLarge from '@picsart/icons/IconChevronRightLarge';
import { useWindowSize } from 'usehooks-ts';

import { theme } from '../../../styles';
import IconRound from '../../core/IconRound';

import useStyles from './styles';
import ImageWithTextScrollableProps from './types';

const GAP = 24;
const IMAGE_WIDTH = 312;
const MOVE_UNIT = IMAGE_WIDTH + GAP;
const MAX_ITEMS_IN_VIEW_WHEN_SCROLLED = 3;

function ImageWithTextScrollable({
  className,
  title,
  items,
  TitleTag = 'h2',
  ItemTitleTag = 'h3',
  cta,
  contentClassName,
}: ImageWithTextScrollableProps) {
  const classes = useStyles();
  const [leftArrow, setLeftArrowVisibility] = useState(false);
  const [rightArrow, setRigthArrowVisibility] = useState(false);
  const [heightOffset, setHeightOffset] = useState(0);
  const [showArrows, setShowArrows] = useState(false);
  const ref = useRef(null);
  const titleRef = useRef(null);
  const { width } = useWindowSize();

  const [margin, setMargin] = useState(0);
  const [contentStyle, setContentStyle] = useState({});
  useEffect(() => {
    if (width && ref.current.clientWidth) {
      if (width > theme.breakpoints.laptop && width < ref.current.scrollWidth) setShowArrows(true);
      else setShowArrows(false);
    }
  }, [width, ref?.current?.clientWidth]);
  const moveLeft = () => {
    setMargin(margin - MOVE_UNIT);
  };
  const moveRight = () => {
    setMargin(margin + MOVE_UNIT);
  };

  useEffect(() => {
    if (!showArrows) {
      return;
    }
    if (margin < MOVE_UNIT / 2 && margin !== 0) {
      setRigthArrowVisibility(true);
    } else {
      setRigthArrowVisibility(false);
    }

    if (-margin >= (items.length - 1 - MAX_ITEMS_IN_VIEW_WHEN_SCROLLED) * MOVE_UNIT) {
      setLeftArrowVisibility(false);
    } else {
      setLeftArrowVisibility(true);
    }
  }, [items.length, margin, showArrows]);

  useEffect(() => {
    if (!titleRef.current) {
      return;
    }
    const offset = titleRef.current.clientHeight + 111 + 24;

    setHeightOffset(offset);
  }, [width, titleRef.current]);

  useEffect(() => {
    setContentStyle(
      showArrows
        ? { marginLeft: margin, justifyContent: 'flex-start' }
        : {
            justifyContent: width < theme.breakpoints.laptop ? 'normal' : 'center',
          },
    );
  }, [margin, showArrows, width]);

  return (
    <section className={cls(classes.root, className)}>
      <div className={classes.container}>
        <div className={classes.btnContainer} style={{ top: heightOffset }}>
          {leftArrow && (
            <IconRound
              className={classes.toRightBtn}
              onClick={moveLeft}
              Icon={IconChevronRightLarge}
            />
          )}
          {rightArrow && (
            <IconRound
              className={classes.toLeftBtn}
              onClick={moveRight}
              Icon={IconChevronLeftLarge}
            />
          )}
        </div>

        <TitleTag className={cls('fs-42', classes.title)} ref={titleRef}>
          {title}
        </TitleTag>

        <div className={classes.contentContainer}>
          <div className={cls(classes.content, contentClassName)} ref={ref} style={contentStyle}>
            {items.map((item, i) => (
              <div className={classes.item} key={`iwts-${i}`}>
                <LazyLoadImage className={classes.img} src={item.src} alt={item.alt} />

                <ItemTitleTag className={cls('fs-20', classes.itemTitle)}>
                  {item.title}
                </ItemTitleTag>
                <p className={'fs-16'}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {cta && <div className={classes.cta}>{cta}</div>}
    </section>
  );
}

export default ImageWithTextScrollable;
